<template>
  <div>
    <datatable-bulk-actions
      v-if="selectedRows.length"
      v-model="actionTarget"
      type="source"
      :rows="selectedRows"
      :total="totalCount"
      :actions="bulkActions"
      @merge="onBulk('merge')"
      @export="onBulk('export')"
      @edit="onBulk('edit')"
      @delete="onBulk('delete')"
      @cancel="closeModalAction"
    />

    <div class="d-flex flex-wrap justify-content-between mb20">
      <div class="source-table-top">
        <div class="source-search">
          <Search
            v-model="filters.name"
            :focus="true"
            :inside-card="true"
            @search="onSearch"
            @clearSearch="onSearch"
          />
        </div>
        <div class="card source-date">
          <date-range-picker
            name="from"
            :value="range"
            inputType="date"
            valueType="YYYY-MM-DD"
            :range="true"
            format="MMM DD YYYY"
            :clearable="true"
            range-separator=" - "
            @onInputChange="onDateInputChanged"
            calendarButtonIcon="fas fa-calendar-alt"
          ></date-range-picker>
        </div>
      </div>
      <button
        class="btn btn-carma btn-square pull-right mt10"
        @click="createSource=true"
      >
        <i class="fas fa-plus"></i>
        <span>Add Source</span>
      </button>
    </div>
    <div class="toggle-unused-source">
      <input-toggle
        :checked="filters.unused_source == 1"
        :onChange="onChangeUnusedSource"
        v-tooltip="getTooltipText()"
        :disabled="disableToggle"
      />
      <span>{{$t("source.include_unused_source")}}</span>
    </div>

    <div class="card">
      <datatable
        :key="`table_list_${tableKey}`"
        ref="table"
        :loading="sourceLoading"
        :data="sourceTableData"
        :updatingRow="updatingRowId"
        :genericRecords="totalCount"
        :bulk-edit="true"
      />
      <div class="loading-next-page" v-if="!sourceLoading && sourceIsBusy"><loader /></div>
    </div>

    <side-panel v-if="showSidebar" :onClose="closeSidePanel">

      <form-renderer
          v-if="activeSidePanel === 'update'"
          id="edit-source-form"
          title="tag.form.edit_source.title"
          formGroupClass="form-group-full"
          :fields="['title', 'first_name', 'middle_name', 'last_name', 'job_title', 'display_name', 'affiliation_id', 'cat_id']"
          :fields-additions="fieldsAdditions"
          :model="updatedRow"
          @onSave="saveupdatedSource"
      />

      <confirm-delete
          v-else-if="activeSidePanel === 'delete'"
          :label="deletedRowLabel"
          :title="'messages.confirm_delete.title_remove'"
          :message="'messages.confirm_delete.message_remove'"
          :deleteButtonLabel="'remove'"
          :onDelete="deleteSource"
      />

      <form-renderer
          v-if="createSource"
          title="sources.add_source_header"
          formGroupClass="form-group-full"
          :fields="['title', 'display_name', 'first_name', 'middle_name', 'last_name', 'job_title', 'affiliation_id', 'cat_id']"
          :fields-additions="createFieldsAdditions"
          :model="updatedRow"
          @onSave="saveSource"
      />

      <div v-else-if="bulkAction === 'edit'">
        <div class="selected-label">
          <h3>Edit selected tags</h3>
          <p>All {{selectedLabel}} will be updated once you submit.</p>
        </div>
        <div class="form form--sidepanel">
          <div class="form-group">
            <label for="tag-id">Tag Approved?</label>
            <input-select
                name="tag-id"
                v-model="selectedApprovalState"
                :options="approvalOptions"
                @input="(value) => bulkSourceUpdate('approved', value)"
            />
          </div>
        </div>
      </div>

      <div v-else-if="bulkAction === 'merge'">
        <template>
          <load-content :loading="isBusy">
            <div class="selected-label">
              <h3>{{this.$i18n.t('merge_source.merge_action')}} {{selectedLabel}}?</h3>
              <p>{{this.$i18n.t('merge_source.merge_message')}}</p>
            </div>
            <div class="form form--sidepanel">
              <div class="form-group">
                <label for="tag-id">{{this.$i18n.t('merge_source.merge')}}</label>
                <input-select
                    v-model="mergeId"
                    :options="selectedRowsOptions"
                    open-direction="bottom"
                    :searchable="true"
                />
              </div>          
              <password-prompt ref="prompt" @isValid="handleIsValidPassword" authAction="merge_sources" />
              <div class="form__group field">
                <input type="button" :disabled="!isPasswordValid || validatingPassword" value="Save" class="btn btn-carma" @click="onMergeTags"/>
              </div>
            </div>
          </load-content>
        </template>
      </div>

      <div v-else-if="bulkAction === 'delete'">
        <confirm-delete
          :label="deleteSelectedLabel"
          :bulk="true"
          :onDelete="bulkDelete"
        />
      </div>

      <form-export-resource v-else-if="bulkAction === 'export'"
        type="source"
        :fields="['title', 'first_name', 'last_name', 'job_title', 'affiliation', 'category', 'associated_articles']"
        :ids="selectedRowsIds"
        :params="exportParams"
      />

    </side-panel>

  </div>
</template>

<script>
import Vue from 'vue';
import {mapState, mapActions} from 'vuex'
import {FIELD_TYPE, VALIDATIONS, BULK_UPDATE} from "@/config/const";
import datatable from '@/components/datatable';
import Search from "@/components/Search.vue";
import formRenderer from '@/components/form-renderer';
import sidePanel from '@/components/side-panel';
import confirmDelete from "@/components/confirm-delete";
import inputSelect from '@/components/form-components/input-select';
import LoadContent from '@/components/load-content';
import FormExportResource from "@/components/coding-admin/form-export-resource";
import DatatableBulkActions from '@/components/datatable-bulk-actions';
import PasswordPrompt from '@/components/password-prompt';
import _ from "lodash";
import DateRangePicker from "@/components/date-picker-2";
import InputToggle from "@/components/form-components/input-toggle";
import moment from 'moment';
import Loader from "@/components/loader";

export default {
  name: "sources",
  created() {
    this.callGetSources();
    this.getAffiliations({projectUid: this.projectUid});
    this.getSourceCategories({projectId: this.projectUid});
  },
  mounted() { window.addEventListener('scroll', this.scroll) },
  beforeDestroy() {
    window.removeEventListener('scroll', this.scroll);
  },
  data() {
    return {
      updatingRowId: null,
      deletedRowId: null,
      selectedRows: [],
      createSource: false,
      deleteTag: false,
      deleteTagObj: null,
      bulkAction: null,
      mergeId: '',
      pagination: {
        page: 1,
        perPage: 20,
        order: "id",
        sort: "asc"
      },
      filters: {
        name: '',
        fromDate: null,
        toDate: null,
        unused_source: false
      },
      selectedApprovalState: null,
      approvalOptions: [
        { value: 'true', label: 'Approved' },
        { value: 'false', label: 'Not Approved' }
      ],
      actionTarget: BULK_UPDATE.ACTION_TARGET.SELECTED,
      validatingPassword: false,
      disableToggle: true,
      isPasswordValid: false,
      tableKey: 0
    }
  },
  computed: {
    ...mapState({
      isBusy: state => state.projectSetup.projectSetup.isBusy,
      project: state => state.projects.project.data,
      sources: state => state.projectSetup.projectSetup.data && state.projectSetup.projectSetup.data.sources || [],
      sourcesPagination: state => state.projectSetup.projectSetup.data && state.projectSetup.projectSetup.data.sourcesPagination || {},
      loading: state => state.projectSetup.projectSetup.isBusy,
      affiliations: state => state.projectSetup.projectSetup.data && state.projectSetup.projectSetup.data.affiliations || [],
      categories: state => state.sourceCategories.sourceCategories.data || [],
      sourceIsBusy: state => state.projectSetup.projectSetup.sourceIsBusy,
    }),
    projectUid() { return this.$route.params.project_uid },
    updatedRow() { return this.sources.find(source => source.id === this.updatingRowId )},
    deletedRow() { return this.sources.find(source => source.id === this.deletedRowId )},
    deletedRowLabel() { return this.deletedRow ? `${this.deletedRow.first_name} ${this.deletedRow.last_name}` : ''},
    activeSidePanel() { return this.updatedRow ? 'update' : this.deletedRow ? 'delete' : null },
    selectedLabel() { return this.selectedRows.length && this.selectedRows.length > 1 ?
        `${this.selectedRows.length} Selected Sources` : 'Selected Source' },
    deleteSelectedLabel() { return this.selectedLabel + ` (${this.selectedRows.length})`},
    selectedRowsOptions() {
      return this.selectedRows.map(option => ({
        value: option.id,
        label: this.getSourceOptionLabel(option),
      }));
    },

    bulkActions () {
      const actions = [
        {
          type: 'merge',
          enabled: this.selectedRows.length > 1 && this.actionTarget === BULK_UPDATE.ACTION_TARGET.SELECTED,
          icon: 'fas fa-layer-group'
        },
        {
          type: 'export',
          enabled: true,
          icon: 'fas fa-download'
        },
        {
          type: 'edit',
          enabled: this.actionTarget === BULK_UPDATE.ACTION_TARGET.SELECTED,
          icon: 'fas fa-edit'
        },
        {
          type: 'delete',
          class: 'text-red',
          enabled: this.actionTarget === BULK_UPDATE.ACTION_TARGET.SELECTED,
          icon: 'fas fa-trash'
        }
      ]
      return actions;
    },

    exportParams () {
      const params = {};
      if (this.actionTarget === BULK_UPDATE.ACTION_TARGET.FILTERED) {
        params.filtered = true;
        params.query = this.filters.name;
      }
      return params;
    },

    totalCount () {
      return (this.sourcesPagination && this.sourcesPagination.totalCount) || 0;
    },

    showSidebar() { return this.updatingRowId || this.deletedRowId || this.bulkAction || this.createSource },

    sourceTableData() {
      return {
        columns: [
          {
            key: "title",
            label: "Title",
            orderable: true,
          },
          {
            key: "first_name",
            label: "First Name",
            orderable: true,
            cellStyle: {
              'max-width': "150px",
            },
            style: {
              'max-width': "150px",
            },
            input: {
              name: "first_name",
              type: FIELD_TYPE.INPUT,
              styleNames: { color: "#ec9148", border: "none" },
              classNames: "input-name",
              placeholder: "first name",
              onBlur: (value, key, row) => {
                this.updateRow(value, key, row); 
              }
            }
          },
          {
            key: "last_name",
            label: "Last Name",
            orderable: true,
            cellStyle: {
              'max-width': "150px",
            },
            style: {
              'max-width': "150px",
            },
            input: {
              name: "last_name",
              type: FIELD_TYPE.INPUT,
              styleNames: { color: "#ec9148", border: "none" },
              classNames: "input-name",
              placeholder: "last name",
              onBlur: (value, key, row) => {
                this.updateRow(value, key, row);
              }
            }
          },
          {
            key: "job_title",
            label: "Job Title",
            orderable: true
          },
          {
            key: 'article_count',
            label: 'nº associated articles ',
            orderable: true,
            render: ({article_count, id} = {}) => {
              return (article_count > 0 ? <router-link style="color:#ec9148;" to={`/analysis/projects/${this.$route.params.project_uid}/sources/${id}/articles?page=1`}>{article_count}</router-link> : <span>{article_count}</span>)
            }
          },
          {
            key: "affiliation_name",
            label: "Affiliation",
            orderable: true,
          },
          {
            key: "category_name",
            label: "Category",
            orderable: true,
            render: (row) => {
              const category = this.categories
                .find(item => item.value === row.cat_id);

              return (<span>{category?.label}</span>)
            }
          },
          {
            key: 'approved',
            label: "source approved",
            style: { 'min-width': '200px' },
            orderable: true,
            input: {
              name: "approved",
              type: FIELD_TYPE.TOGGLE,
              onChange: (value, key, row) => {
                this.updateRow(value, key, row);
              }
            },
          },
          {
            key: 'last_article',
            label: this.$t('source.table.last_article'),
            orderable: true,
            render: ({analysis_article_id, last_article_date} = {}) => {
              return (analysis_article_id ? <span>{moment.utc(last_article_date).format('YYYY-MM-DD')} <a style="color:#ec9148;" href={`/analysis/editor/${this.$route.params.project_uid}/${analysis_article_id}`} target="_blank"><i class="far fa-link"></i></a> </span>: null)
            }
          },
          {
            Key: "",
            label: "",
            input: {
              name: "name",
              type: FIELD_TYPE.BUTTON,
              label_button: "...",
              onClick:(key, row) => {
                this.setUpdatingRow(row);
              }
            }
          }
        ],
        body: this.sources,
        resizable: true,
        removeRow: {
          enabled: true,
          onClick: (row) => this.setDeletedRow(row.id)
        },
        addRow: {
          enabled: this.sources.length > 12,
          label: this.$i18n.t('tag.form.create.title', {resource: 'Source'}), // 'Add Grouping',
          onClick: () => this.setCreateSource(true)
        },
        order: {
          column: this.pagination.order,
          direction: this.pagination.sort,
          onChange: (column, direction) => {
            this.onSortTable({ order: column, sort: direction });
          }
        },
        bulkActions: {
          enabled: true,
          onSelect: (rows) => {
            this.setSelectedRows(rows)
          }
        },
      }
    },
    fieldsAdditions() {
      return {
        title: {
          type: FIELD_TYPE.SELECT,
          translatePlaceholder: 'sources.select_title',
          style: 'order: 1;',
          customClass: 'mr10'
        },
        job_title: {
          style: 'flex-basis: 100%; order: 2;',
        },
        first_name: {
          style: 'flex-grow: 3; order: 3;',
        },
        middle_name: {
          style: 'flex-basis: 100%; order: 4;',
        },
        last_name: {
          style: 'flex-basis: 100%; order: 5;',
        },
        display_name: {
          style: 'flex-basis: 100%; order: 6;',
        },
        affiliation_id: {
          options: this.affiliations,
          optionValue: 'id',
          optionLabel: 'name',
          searchable: true,
          optionsLimit: 100,
          dropdownDirection: 'top',
          placeholder: this.$i18n.t('sources.affiliation.select'),
          hideSelected: false,
          style: 'flex-basis: 50%; flex-grow: 0; order: 7;',
          customClass: 'mr10',
          customLabel: this.displayLabel,
          trackBy: 'id',
          taggable: true,
          tagPlaceholder: "Press enter to create affiliation",
          validate: [VALIDATIONS.REQUIRED]
        },
        cat_id: {
          options: this.categories || [],
          placeholder: this.$i18n.t('sources.category.select'),
          style: 'flex-basis: 48%; flex-grow: 0; order: 8;',
          customLabel: this.displayLabel,
          validate: [VALIDATIONS.REQUIRED],
        }
      }
    },
    createFieldsAdditions() {
      return {
        ...this.fieldsAdditions,
        ...{
          job_title: {
            style: 'flex-basis: 100%; order: 2;',
            onBlurOnly: true
          },
          first_name: {
            style: 'flex-grow: 3; order: 3;',
            onBlurOnly: true
          },
          middle_name: {
            style: 'flex-basis: 100%; order: 4;',
            onBlurOnly: true
          },
          last_name: {
            style: 'flex-basis: 100%; order: 5;',
            onBlurOnly: true
          },
          display_name: {
            style: 'flex-basis: 100%; order: 6;',
            onBlurOnly: true
          },
        }
      }
    },

    selectedRowsIds() {
      return this.selectedRows.map(row => row.id);
    },
    range () {
      return [moment.utc(this.filters.fromDate).format('YYYY-MM-DD'), moment.utc(this.filters.toDate).format('YYYY-MM-DD')];
    },
    sourceLoading() {
      return this.sourceIsBusy === true && this.pagination.page === 1;
    },
  },
  methods: {
    ...mapActions({
      getSources: "projectSetup/getSources",
      getAffiliations: "projectSetup/getSourcesAffiliations",
      getSourceCategories: "sourceCategories/getSourceCategories",
      addSource: "projectSetup/addSource",
      updateSource: "projectSetup/updateSource",
      removeSource: "projectSetup/removeSource",
      mergeTags: "projectSetup/mergeTags",
      updateBulkSource: "projectSetup/updateBulkSourceByProject",
      deleteBulkSources: "projectSetup/deleteBulkSources"
    }),

    callGetSources() {
      this.getSources({
        projectUid: this.projectUid,
        pagination: this.pagination,
        filters: this.filters
      });
    },

    onSearch() {
      this.pagination.page = 1;
      this.callGetSources();
    },
    onDateInputChanged: function(value) {
      this.filters.fromDate = value[0];
      this.filters.toDate = value[1];
      this.pagination.page = 1;
      this.disableToggle = false;
      if(!this.filters.toDate || !this.filters.fromDate){
        this.disableToggle = true;
        this.filters.unused_author = 0;
      }
      this.callGetSources();
    },
    onChangeUnusedSource: function(value) {
      this.pagination.page = 1;
      this.filters.unused_source = value == true ? 1 : 0;
      this.getSources({
        projectUid: this.projectUid,
        pagination: this.pagination,
        filters: this.filters
      });
    },
    getTooltipText: function() {
      if (this.disableToggle) {
        return this.$t('source.tooltips.unused_source_disabled');
      } else {
        return this.$t('source.tooltips.unused_source');
      }
    },

    setDeletedRow(id) { this.deletedRowId = id },
    setSelectedRows(rows) { this.selectedRows = rows },
    setUpdatingRow(row) { this.updatingRowId = row.id },
    setCreateSource(state) { this.createSource = state },

    getSourceOptionLabel(source) {
      const name = source.name || `${source.first_name} ${source.last_name}`;
      const countInfo = this.$tc("articles.count", source.article_count, {
        count: source.article_count,
      });

      return `Name: ${name} | ID: ${source.id} | Category: ${source.category_name} | Aff: ${source.affiliation_name} | ${countInfo}`;
    },

    displayLabel (option) {
      return `${option.label}`
    },

    updateRow(value, key, row) {
      const source = {
        ...row,
        [key]: value
      };
      this.updateSource({ projectUid: this.projectUid, source, pagination: this.pagination });
    },
    deleteSource() {
      this.removeSource({projectUid: this.projectUid, sourceId: this.deletedRowId})
      .then(()=> {
        this.selectedRows = this.selectedRows.filter(item => 
          item.id !== this.deletedRowId
        );
        this.$refs.table.selectedRows = this.selectedRows;  
        this.closeSidePanel();
      })
    },
    closeSidePanel() {
      this.bulkAction = null;
      this.updatingRowId = null
      this.deletedRowId = null
      this.createSource = null
    },
    saveupdatedSource(source) {
      this.updateSource({ projectUid: this.projectUid, source })
      .then(() => {
        this.closeSidePanel();
        this.reloadAffiliations(source);
      });
    },
    saveSource(source) {
      source.categoryId = source.cat_id
      this.addSource({ projectUid: this.projectUid, source })
      .then(() => {
        this.closeSidePanel();
        this.reloadAffiliations(source);
      });
    },
    reloadAffiliations(model) {
      if (typeof model.affiliation_id === 'string')
        this.getAffiliations({ projectUid: this.projectUid })
    },
    onSortTable: async function({order, sort}) {
      this.pagination.order = order;
      this.pagination.sort = sort;
      this.pagination.page = 1;
      
      this.getSources({
        projectUid: this.projectUid,
        pagination: this.pagination,
        filters: this.filters
      })
    },
    onBulk(action) {
      this.bulkAction = action;
    },
    async onMergeTags() {
      try {
        this.validatingPassword = true;
        await this.$refs.prompt.validate();
        this.mergeTags({
          projectUid: this.projectUid,
          tagId: this.mergeId,
          selectedTags: this.selectedRows,
          categoryIsSource: true,
          pagination: this.pagination})
          this.selectedRows = [];
          this.bulkEditOpen = false;
          this.closeSidePanel();
        } catch (error) {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: this.$t("messages.confirm_delete.password_confirm_error")
        })
      } finally { this.validatingPassword = false; }
    },
    bulkSourceUpdate(field, value) { // TODO: resolve the store returning data + request paginated data on this resource
      let bulkTags = _.cloneDeep(this.selectedRows)
      bulkTags.forEach(row => row[field] = value)
      this.pagination.page = 1
      this.updateBulkSource({ projectUid: this.projectUid, bulkTags, pagination: this.pagination }) //, pagination: this.pagination
      .then(() => {
        this.closeSidePanel();
        this.selectedRows = [];
      })
    },
    bulkDelete: function(){
      this.deleteBulkSources({projectUid: this.projectUid, bulkTags: this.selectedRows, pagination: this.pagination})
      .then(() => {
        this.closeSidePanel();
        this.selectedRows = [];
      });
    },
    scroll: function() {
      const { currentPage, totalPages } = this.sourcesPagination;
      if((currentPage < totalPages) && !this.sourceIsBusy) {
        const wHeight = window.innerHeight;
        const {top, height} = this.$refs.table.$el.getBoundingClientRect();
        const margin = 100;
        if(top * -1 + wHeight >= height - margin){
          this.pagination.page += 1
          this.getSources({projectUid: this.projectUid, pagination: this.pagination, filters: this.filters})
        }
      }
    },
    handleIsValidPassword: function (val){
      this.isPasswordValid = val;
    },
    closeModalAction() {
      this.selectedRows = [];
      this.tableKey++;
    },
  },
  components: {
    Search,
    datatable,
    formRenderer,
    sidePanel,
    confirmDelete,
    inputSelect,
    LoadContent,
    FormExportResource,
    DatatableBulkActions,
    DateRangePicker,
    InputToggle,
    PasswordPrompt,
    Loader
  }
}
</script>

<style lang="scss" scoped>

.selected-label{
  margin-top: 30px;
}

.div-categories-tag {
  display: flex;
  flex-direction: row;
  margin-bottom: 25px;
  margin-top: 10px;
}

.source-table-top {
  display: flex;
  align-items: center;
}

.source-search,
.source-date {
  margin-right: 20px;
}
.source-date{
  width: 300px;
  margin-top: 15px;
  .mx-datepicker-range {
    &:deep(.mx-input) {
      border: 0;
      outline: 0;
      box-shadow: none;
      font-size: inherit;
      color: inherit;
      &:hover {
        background-color: #f1f1f1;
      }
    }
  }
}
.toggle-unused-source{
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 5px;
  & > span {
    font-size: 13px;
    margin-left: 10px;
  }
}

@import "~@/styles/components/coding-admin/tags";

:deep(.source-affiliation-select.multiselect) {
  font-size: 0;
  padding: 0;
  margin: 0;
  min-height: 0;
  height: auto;

  .multiselect__select {
    top: calc(50% - 14px);
    line-height: 0;
  }

  .multiselect__tags {
    padding: 0;
    margin: 0;
    min-height: 0;
    border-radius: 5px;
    padding: 20px;

    .multiselect__single,
    .multiselect__placeholder {
      font-size: 16px;
      line-height: 1.4;
    }
  }
}

</style>